import { baseApi } from '@store/baseApi';
import { NotificationState } from '../types';
import { createSelector } from '@reduxjs/toolkit';

/**
 * Feature-specific tag types for notifications
 */
const NOTIFICATION_TAG_TYPES = ['Notifications'] as const;

type CheckNewNotificationsResponse = {
  user_notifications: NotificationState[];
};

/**
 * Notifications API - extends the base API with notification-specific endpoints
 */
export const notificationsApi = baseApi
  .enhanceEndpoints({
    addTagTypes: NOTIFICATION_TAG_TYPES,
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Get all notifications for the current user
      checkNewNotifications: builder.query<NotificationState[], void>({
        query: () => ({
          url: '/user/checkNewNotifications',
          method: 'GET',
        }),
        transformResponse: (response: CheckNewNotificationsResponse) =>
          response.user_notifications || [],
        providesTags: ['Notifications'],
      }),

      // Mark a notification as viewed
      markNotificationViewed: builder.mutation<void, string>({
        query: notificationId => ({
          url: '/user/notificationViewed',
          method: 'POST',
          body: { notificationId },
        }),
        invalidatesTags: ['Notifications'],
        // Add optimistic update
        onQueryStarted: async (notificationId, { dispatch, queryFulfilled }) => {
          // Apply optimistic update
          const patchResult = dispatch(
            notificationsApi.util.updateQueryData('checkNewNotifications', undefined, draft => {
              const notification = draft.find(n => n.notification_id === notificationId);
              if (notification) {
                notification.isOpened = 1;
              }
            }),
          );

          try {
            // Wait for the actual API request to complete
            await queryFulfilled;
          } catch {
            // If the mutation fails, revert the optimistic update
            patchResult.undo();
          }
        },
      }),

      // Remove a notification
      removeNotification: builder.mutation<void, string>({
        query: notificationId => ({
          url: '/user/removeNotification',
          method: 'POST',
          body: { notificationId },
        }),
        invalidatesTags: ['Notifications'],
        // Add optimistic update
        onQueryStarted: async (notificationId, { dispatch, queryFulfilled }) => {
          // Apply optimistic update
          const patchResult = dispatch(
            notificationsApi.util.updateQueryData('checkNewNotifications', undefined, draft => {
              const index = draft.findIndex(n => n.notification_id === notificationId);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            }),
          );

          try {
            // Wait for the actual API request to complete
            await queryFulfilled;
          } catch {
            // If the mutation fails, revert the optimistic update
            patchResult.undo();
          }
        },
      }),
    }),
    overrideExisting: false,
  });

// Create a selector to get notifications from the RTK Query cache
const selectNotificationsResult = notificationsApi.endpoints.checkNewNotifications.select();

// Create a memoized selector for the notifications data
export const selectNotificationsData = createSelector(
  selectNotificationsResult,
  result => result?.data ?? [],
);

// Create a memoized selector for the unread count
export const selectUnreadCount = createSelector(
  selectNotificationsData,
  notifications => notifications.filter(n => n.isOpened === 0).length,
);

// Export hooks for the notifications API
export const {
  useCheckNewNotificationsQuery,
  useMarkNotificationViewedMutation,
  useRemoveNotificationMutation,
} = notificationsApi;

// Custom hook for getting unread notifications count
export const useNotificationsUnreadCount = () => {
  const { data: notifications = [] } = useCheckNewNotificationsQuery();
  return notifications.filter(n => n.isOpened === 0).length;
};

// --- Notification Summary (v2 generated endpoint) ---

import { enhancedApi as notificationsGeneratedApi } from '@store/generated/notifications';

/**
 * Notification Summary API - enhances the generated endpoint with cache tags.
 * Used for badge counts on nav and queue tabs.
 */
export const notificationSummaryApi = notificationsGeneratedApi.enhanceEndpoints({
  addTagTypes: ['NotificationSummary'] as const,
  endpoints: {
    getV2ProviderNotificationsSummary: {
      providesTags: ['NotificationSummary'],
    },
  },
});

export const {
  useGetV2ProviderNotificationsSummaryQuery: useNotificationSummaryQuery,
} = notificationSummaryApi;
